import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import sentry from '@/src/utils/sentry';

type LoadStripeOptions = Parameters<typeof loadStripe>[1];
type LoadStripeFn = typeof loadStripe;
type SleepFn = (ms: number) => Promise<void>;

export const STRIPE_LOAD_MAX_ATTEMPTS = 3;
export const STRIPE_LOAD_BACKOFF_MS = [1000, 2000, 4000] as const;

const DEFAULT_PROBABLE_ROOT_CAUSE =
  'Stripe.js CDN request blocked or unavailable: CSP, ad blocker, network timeout, or CDN outage.';

let stripePromiseCache: Promise<Stripe | null> | null = null;
let stripePromiseCacheKey: string | null = null;

const defaultSleep: SleepFn = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getUserAgent = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.userAgent || 'unknown';
};

const normalizeError = (error: unknown) => {
  if (error instanceof Error) return error;
  return new Error(String(error ?? 'Failed to load Stripe.js'));
};

const buildCacheKey = (publishableKey: string, options?: LoadStripeOptions) => {
  const locale = options && 'locale' in options ? String(options.locale ?? '') : '';
  return `${publishableKey}:${locale}`;
};

export async function loadStripeWithRetry(
  publishableKey: string,
  options?: LoadStripeOptions,
  config?: {
    loadStripeFn?: LoadStripeFn;
    sleepFn?: SleepFn;
    maxAttempts?: number;
    backoffMs?: readonly number[];
  },
): Promise<Stripe | null> {
  const loadStripeFn = config?.loadStripeFn ?? loadStripe;
  const sleepFn = config?.sleepFn ?? defaultSleep;
  const maxAttempts = config?.maxAttempts ?? STRIPE_LOAD_MAX_ATTEMPTS;
  const backoffMs = config?.backoffMs ?? STRIPE_LOAD_BACKOFF_MS;
  const userAgent = getUserAgent();
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    sentry.addBreadcrumb({
      category: 'stripe',
      level: 'info',
      message: 'Loading Stripe.js',
      data: {
        stripe_load_attempt: attempt,
        user_agent: userAgent,
      },
    });

    try {
      const stripe = await loadStripeFn(publishableKey, options);
      if (stripe) return stripe;
      throw new Error('Stripe.js returned null');
    } catch (error) {
      lastError = normalizeError(error);

      if (attempt >= maxAttempts) {
        break;
      }

      const delayMs = backoffMs[attempt - 1] ?? backoffMs[backoffMs.length - 1] ?? 0;
      if (delayMs > 0) {
        await sleepFn(delayMs);
      }
    }
  }

  const finalError = lastError ?? new Error('Failed to load Stripe.js');
  sentry.captureException(finalError, (scope) => {
    scope.setTag('stripe_load_failed', 'true');
    scope.setExtra('stripe_load_attempt', maxAttempts);
    scope.setExtra('user_agent', userAgent);
    scope.setExtra('probable_root_cause', DEFAULT_PROBABLE_ROOT_CAUSE);
    return scope;
  });

  return null;
}

export function getStripePromiseWithRetry(
  publishableKey: string,
  options?: LoadStripeOptions,
): Promise<Stripe | null> {
  const cacheKey = buildCacheKey(publishableKey, options);
  if (!stripePromiseCache || stripePromiseCacheKey !== cacheKey) {
    stripePromiseCache = loadStripeWithRetry(publishableKey, options);
    stripePromiseCacheKey = cacheKey;
  }
  return stripePromiseCache;
}

export function clearStripeLoaderCache() {
  stripePromiseCache = null;
  stripePromiseCacheKey = null;
}

export const resetStripeLoaderForTests = clearStripeLoaderCache;
